import api from "../utils/api";

const rbacService = {
  // Permission API calls
  async getPermissions(params = {}) {
    const response = await api.get("/rbac/permissions", params);
    return response.data;
  },

  async getPermissionById(id) {
    const response = await api.get(`/rbac/permissions/${id}`);
    return response.data;
  },

  async createPermission(permissionData) {
    const response = await api.post("/rbac/permissions", permissionData);
    return response.data;
  },

  async updatePermission(id, data) {
    const response = await api.put(`/rbac/permissions/${id}`, data);
    return response.data;
  },

  async deletePermission(id) {
    const response = await api.delete(`/rbac/permissions/${id}`);
    return response.data;
  },

  // Role API calls
  async getRoles(params = {}) {
    const response = await api.get("/admin/roles", params);
    return response.data;
  },

  async getRoleById(id) {
    const response = await api.get(`/admin/roles/${id}`);
    return response.data;
  },

  async createRole(roleData) {
    const response = await api.post("/admin/roles", roleData);
    return response.data;
  },

  async updateRole(id, data) {
    const response = await api.put(`/admin/roles/${id}`, data);
    return response.data;
  },

  async deleteRole(id) {
    const response = await api.delete(`/admin/roles/${id}`);
    return response.data;
  },

  // Role Permission Management
  async addPermissionToRole(roleId, data) {
    const response = await api.post(`/rbac/roles/${roleId}/permissions`, data);
    return response.data;
  },

  async removePermissionFromRole(roleId, permissionId) {
    const response = await api.delete(
      `/rbac/roles/${roleId}/permissions/${permissionId}`,
    );
    return response.data;
  },

  // Analytics and Reports
  async getRolePermissionMatrix(organizationId) {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get("/rbac/matrix", params);
    return response.data;
  },

  async getUserPermissions(userId, params = {}) {
    const response = await api.get(`/rbac/users/${userId}/permissions`, params);
    return response.data;
  },

  // Dynamic Module Management
  async getModules(params = {}) {
    const response = await api.get("/admin/modules", params);
    return response.data;
  },

  async getModuleById(id) {
    const response = await api.get(`/admin/modules/${id}`);
    return response.data;
  },

  async createModule(moduleData) {
    const response = await api.post("/admin/modules", moduleData);
    return response.data;
  },

  async updateModule(id, data) {
    const response = await api.put(`/admin/modules/${id}`, data);
    return response.data;
  },

  async deleteModule(id) {
    const response = await api.delete(`/admin/modules/${id}`);
    return response.data;
  },

  async toggleModuleStatus(id) {
    const response = await api.patch(`/admin/modules/${id}/toggle`);
    return response.data;
  },

  async getModuleStats() {
    const response = await api.get("/admin/modules/stats");
    return response.data;
  },

  // Dynamic Screen Management
  async getScreens(params = {}) {
    const response = await api.get("/admin/screens", params);
    return response.data;
  },

  async getScreenById(id) {
    const response = await api.get(`/admin/screens/${id}`);
    return response.data;
  },

  async createScreen(screenData) {
    const response = await api.post("/admin/screens", screenData);
    return response.data;
  },

  async updateScreen(id, data) {
    const response = await api.put(`/admin/screens/${id}`, data);
    return response.data;
  },

  async deleteScreen(id) {
    const response = await api.delete(`/admin/screens/${id}`);
    return response.data;
  },

  async toggleScreenStatus(id) {
    const response = await api.patch(`/admin/screens/${id}/toggle`);
    return response.data;
  },

  async getSidebarScreens() {
    const response = await api.get("/admin/screens/sidebar");
    return response.data;
  },

  async getScreensByModule(moduleId) {
    const response = await api.get(`/admin/screens/module/${moduleId}`);
    return response.data;
  },

  async getScreenStats() {
    const response = await api.get("/admin/screens/stats");
    return response.data;
  },

  // Dynamic Permission Management (Admin)
  async getAdminPermissions(params = {}) {
    const response = await api.get("/admin/permissions", params);
    return response.data; // Returns { success: true, data: [permissions], pagination: {...} }
  },

  async getAdminPermissionById(id) {
    const response = await api.get(`/admin/permissions/${id}`);
    return response.data;
  },

  async createAdminPermission(permissionData) {
    const response = await api.post("/admin/permissions", permissionData);
    return response.data;
  },

  async updateAdminPermission(id, data) {
    const response = await api.put(`/admin/permissions/${id}`, data);
    return response.data;
  },

  async deleteAdminPermission(id) {
    const response = await api.delete(`/admin/permissions/${id}`);
    return response.data;
  },

  async togglePermissionStatus(id) {
    const response = await api.patch(`/admin/permissions/${id}/toggle`);
    return response.data;
  },

  async getPermissionsByModule(module) {
    const response = await api.get(`/admin/permissions/module/${module}`);
    return response.data;
  },

  async getSystemPermissions() {
    const response = await api.get("/admin/permissions/system");
    return response.data;
  },

  async getPermissionStats() {
    const response = await api.get("/admin/permissions/stats");
    return response.data;
  },

  // Enhanced Role Management
  async getRoleStats() {
    const response = await api.get("/admin/roles/stats");
    return response.data;
  },

  async getSystemRoles() {
    const response = await api.get("/admin/roles/system");
    return response.data;
  },

  async toggleRoleStatus(id) {
    const response = await api.patch(`/admin/roles/${id}/toggle`);
    return response.data;
  },

  async addPermissionToRoleAdmin(roleId, data) {
    const response = await api.post(`/admin/roles/${roleId}/permissions`, data);
    return response.data;
  },

  async removePermissionFromRoleAdmin(roleId, permissionId) {
    const response = await api.delete(
      `/admin/roles/${roleId}/permissions/${permissionId}`,
    );
    return response.data;
  },

  // User Role Assignment
  async getUsersWithRoles(params = {}) {
    const response = await api.get("/admin/user-roles", params);
    return response.data;
  },

  async getUserRoleById(id) {
    const response = await api.get(`/admin/user-roles/${id}`);
    return response.data;
  },

  async assignRoleToUser(userId, data) {
    const response = await api.post(`/admin/user-roles/${userId}/assign`, data);
    return response.data;
  },

  async removeRoleFromUser(userId) {
    const response = await api.delete(`/admin/user-roles/${userId}/remove`);
    return response.data;
  },

  async getUsersByRole(roleName) {
    const response = await api.get(`/admin/user-roles/role/${roleName}`);
    return response.data;
  },

  async getAvailableRoles() {
    const response = await api.get("/admin/user-roles/roles/available");
    return response.data;
  },

  async getRoleAssignmentStats() {
    const response = await api.get("/admin/user-roles/stats");
    return response.data;
  },

  async bulkAssignRoles(data) {
    const response = await api.post("/admin/user-roles/bulk-assign", data);
    return response.data;
  },

  // Consultant User Management
  async getConsultantUsers(params = {}) {
    const response = await api.get("/admin/consultant-users", params);
    return response.data;
  },

  async getConsultantRoles() {
    const response = await api.get("/admin/consultant-roles");
    return response.data;
  },

  async createConsultantUser(userData) {
    const response = await api.post("/admin/consultant-users", userData);
    return response.data;
  },

  async deleteConsultantUser(userId) {
    const response = await api.delete(`/admin/consultant-users/${userId}`);
    return response.data;
  },

  async toggleConsultantUserStatus(userId) {
    const response = await api.patch(`/admin/consultant-users/${userId}/toggle`);
    return response.data;
  },

  // CRM Consultant Management
  async getConsultantPermissions() {
    const response = await api.get("/consultant-management/crm/permissions");
    return response.data;
  },

  async getConsultantRolesForCRM(params = {}) {
    const response = await api.get("/consultant-management/crm/roles", params);
    return response.data;
  },

  async createConsultantRole(roleData) {
    const response = await api.post("/consultant-management/crm/roles", roleData);
    return response.data;
  },

  async updateConsultantRole(id, data) {
    const response = await api.put(`/consultant-management/crm/roles/${id}`, data);
    return response.data;
  },

  async deleteConsultantRole(id) {
    const response = await api.delete(`/consultant-management/crm/roles/${id}`);
    return response.data;
  },

  async getConsultantStaff(params = {}) {
    const response = await api.get("/consultant-management/crm/staff", params);
    return response.data;
  },

  async createConsultantStaff(staffData) {
    const response = await api.post("/consultant-management/crm/staff", staffData);
    return response.data;
  },

  async updateConsultantStaff(id, data) {
    const response = await api.put(`/consultant-management/crm/staff/${id}`, data);
    return response.data;
  },

  async deleteConsultantStaff(id) {
    const response = await api.delete(`/consultant-management/crm/staff/${id}`);
    return response.data;
  },
};

export default rbacService;
